import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { SignatureBg } from "./SignatureBg";
import { portal, type CompanyBranding } from "../lib/api";

/**
 * Top frame for every page on portal.aliefdisputehub.com.
 *
 * Mirrors the staff brand language — black backdrop, chrome-silver
 * wordmark, faint crimson radial vignette — but no sidebar, no
 * operator chrome, no dense topbar. Used by every Portal* page so
 * the header reads consistently across the client experience.
 */
export function PortalShell({
  children,
  narrow,
  actions,
}: {
  children: ReactNode;
  /** Login/Sent/Verify use a tight, centered card layout. */
  narrow?: boolean;
  /** Right-side topbar actions, e.g. "Sign out" on authed pages. */
  actions?: ReactNode;
}) {
  // White-label: on authed pages this resolves to the client's company brand;
  // pre-auth (login) it falls back to the Alief Dispute Hub platform brand.
  const [brand, setBrand] = useState<CompanyBranding | null>(null);
  useEffect(() => {
    let live = true;
    portal.branding().then((b) => { if (live) setBrand(b); }).catch(() => {});
    return () => { live = false; };
  }, []);
  const name = brand?.business_name || "Alief Dispute Hub";
  const logo = brand?.logo_url || null;  // no default logo image until Tanya provides one
  const tagline = brand?.tagline || "Analyze Smarter. Dispute Better. Powered by AI.";

  return (
    <div className="portal-shell has-signature-bg">
      <SignatureBg variant="mark" />
      <header className="portal-topbar">
        <Link to="/portal/dashboard" className="portal-wordmark" aria-label={`${name} — Client Portal`}>
          {logo && <img src={logo} alt="" aria-hidden="true" />}
          <div className="portal-wordmark-text">
            <strong>{name}</strong>
            <span>{tagline}</span>
          </div>
        </Link>
        {actions && <div className="portal-topbar-actions">{actions}</div>}
      </header>
      <main className={narrow ? "portal-main narrow" : "portal-main"}>{children}</main>
    </div>
  );
}
